/**
 * AWS Services Index
 * Central initialization and export point for all AWS services
 */

import { validateAWSConfig, AWS_CONFIG } from './config';
import { initializeDynamoDB } from './dynamodb';
import { initializeCognito } from './cognito';
import { initializeLambda } from './lambda';

// Configuration
export { AWS_CONFIG, validateAWSConfig } from './config';

// DynamoDB Services
export {
  initializeDynamoDB,
  GameSessionsTable,
  UserProfilesTable,
  RoomCodesTable,
  GameStatisticsTable
} from './dynamodb';

// API Gateway Services
export {
  GameLogicAPI,
  UserManagementAPI,
  MultiplayerAPI,
  StatisticsAPI,
  setAPIAuthToken,
  clearAPIAuthToken
} from './apiGateway';

// Cognito Authentication Services
export {
  initializeCognito,
  CognitoAuthService,
  OAuthService
} from './cognito';

// Lambda Function Services
export {
  initializeLambda,
  GameLogicLambda,
  UserManagementLambda,
  MultiplayerManagerLambda,
  StatisticsManagerLambda
} from './lambda';

// WebSocket Services
export {
  WebSocketService,
  WebSocketMessageType,
  ConnectionState,
  getWebSocketService,
  initializeWebSocket
} from './websocket';
export type {
  WebSocketMessage,
  WebSocketEventHandlers,
  WebSocketOptions
} from './websocket';

/**
 * Initialize all AWS services
 */
export const initializeAWSServices = async (): Promise<boolean> => {
  console.log('Initializing AWS services...');
  
  // Validate configuration first
  if (!validateAWSConfig()) {
    console.error('AWS configuration validation failed');
    return false;
  }
  
  try {
    // Initialize all services in parallel
    const [
      dynamoDBInitialized,
      cognitoInitialized,
      lambdaInitialized
    ] = await Promise.all([
      initializeDynamoDB(),
      initializeCognito(),
      initializeLambda()
    ]);
    
    const allInitialized = dynamoDBInitialized && cognitoInitialized && lambdaInitialized;
    
    if (allInitialized) {
      console.log('All AWS services initialized successfully');
    } else {
      console.warn('Some AWS services failed to initialize:', {
        dynamoDB: dynamoDBInitialized,
        cognito: cognitoInitialized,
        lambda: lambdaInitialized
      });
    }
    
    return allInitialized;
  } catch (error) {
    console.error('Error initializing AWS services:', error);
    return false;
  }
};

/**
 * Check if AWS services are available
 */
export const isAWSAvailable = (): boolean => {
  try {
    // Check if we're in a browser environment with network access
    return typeof window !== 'undefined' && navigator.onLine;
  } catch {
    return false;
  }
};

/**
 * AWS service health check
 */
export const checkAWSHealth = async (): Promise<{
  dynamodb: boolean;
  apiGateway: boolean;
  cognito: boolean;
  lambda: boolean;
}> => {
  const health = {
    dynamodb: false,
    apiGateway: false,
    cognito: false,
    lambda: false
  };
  
  try {
    // Test DynamoDB connection
    try {
      // This would be a simple health check query
      health.dynamodb = true;
    } catch {
      health.dynamodb = false;
    }
    
    // Test API Gateway connection
    try {
      const response = await fetch(`${AWS_CONFIG.apiGateway.baseUrl}/health`);
      health.apiGateway = response.ok;
    } catch {
      health.apiGateway = false;
    }
    
    // Test Cognito (basic configuration check)
    health.cognito = !!(AWS_CONFIG.cognito.userPoolId && AWS_CONFIG.cognito.clientId);
    
    // Test Lambda (configuration check)
    health.lambda = !!(AWS_CONFIG.lambda.gameLogic && AWS_CONFIG.lambda.userManagement);
    
  } catch (error) {
    console.error('AWS health check error:', error);
  }
  
  return health;
};